import { env } from '@/shared/config/env';

export type MobileRegion = 'AE' | 'IN';

export type MobileConfig = {
  region: MobileRegion;
  length: number;
  code: string;
  label: string;
  flag: string;
  /** Full number must match this when complete. */
  regex: RegExp;
  /** Allowed first digit(s). */
  firstDigitRegex: RegExp;
  message: string;
  /** Mask hint shown in inputs (e.g. xxx-xxx-xxxx). */
  placeholder: string;
};

export type MobileParseResult = {
  value: string;
  /** True when the user tried something that does not fit the regional regex. */
  rejected: boolean;
};

const AE_MOBILE_CONFIG: MobileConfig = {
  region: 'AE',
  length: 9,
  code: '+971',
  label: 'AE +971',
  flag: '🇦🇪',
  regex: /^[5]\d{8}$/,
  firstDigitRegex: /^5$/,
  message: 'Enter a valid AE mobile (9 digits, starts with 5)',
  placeholder: 'xxx-xxx-xxx',
};

const IN_MOBILE_CONFIG: MobileConfig = {
  region: 'IN',
  length: 10,
  code: '+91',
  label: 'IN +91',
  flag: '🇮🇳',
  regex: /^[6-9]\d{9}$/,
  firstDigitRegex: /^[6-9]$/,
  message: 'Enter a valid Indian mobile (10 digits, starts with 6–9)',
  placeholder: 'xxx-xxx-xxxx',
};

export function isAERegion() {
  return env.COUNTRY === 'UAE';
}

export function getMobileConfig(): MobileConfig {
  return isAERegion() ? AE_MOBILE_CONFIG : IN_MOBILE_CONFIG;
}

/** Resolve India / UAE rules from a dial code (+91 / +971). */
export function getMobileConfigByCode(code?: string | null): MobileConfig {
  const normalized = String(code || '').replace(/\s/g, '');
  if (normalized.includes('971')) return AE_MOBILE_CONFIG;
  if (normalized.includes('91')) return IN_MOBILE_CONFIG;
  return getMobileConfig();
}

/**
 * Display digits as xxx-xxx-xxxx (IN) or xxx-xxx-xxx (AE).
 * Works for partial input while typing.
 */
export function formatMobileDisplay(
  digits: string,
  config: MobileConfig = getMobileConfig(),
): string {
  const d = String(digits || '').replace(/\D/g, '').slice(0, config.length);
  if (!d) return '';

  if (config.region === 'AE') {
    const parts = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 9)].filter(Boolean);
    return parts.join('-');
  }

  const parts = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 10)].filter(Boolean);
  return parts.join('-');
}

/** Read-only style: "+91 987-654-3210" */
export function formatMobileWithCountryCode(
  digits?: string | null,
  countryCode?: string | null,
): string {
  const config = countryCode
    ? getMobileConfigByCode(countryCode)
    : getMobileConfig();
  const formatted = formatMobileDisplay(String(digits || ''), config);
  if (!formatted) return '';
  return `${countryCode || config.code} ${formatted}`.trim();
}

/**
 * Parse mobile input against regional rules.
 * Dashes/spaces are treated as formatting (not rejected).
 * Invalid / excluded characters are dropped and `rejected` is set so the UI can show an error.
 */
export function parseMobileInput(
  raw: string,
  config: MobileConfig = getMobileConfig(),
): MobileParseResult {
  const withoutFormatting = String(raw || '').replace(/[\s-]/g, '');
  const hasInvalidChars = /\D/.test(withoutFormatting);
  const digits = String(raw || '').replace(/\D/g, '');
  let value = '';
  let rejected = hasInvalidChars;

  for (const ch of digits) {
    if (value.length >= config.length) {
      rejected = true;
      break;
    }

    if (value.length === 0) {
      if (config.firstDigitRegex.test(ch)) {
        value += ch;
      } else {
        rejected = true;
      }
      continue;
    }

    value += ch;
  }

  return { value, rejected };
}

export function isValidMobile(
  value: string,
  config: MobileConfig = getMobileConfig(),
) {
  return config.regex.test(String(value || '').replace(/\D/g, ''));
}
